import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSettingsStore } from '@/stores/settingsStore'
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore'
import { revealSettingsAnchor, SETTINGS_PANEL_ID, SETTINGS_PANEL_MOUNT_MS } from '@/lib/settingsAnchors'
import type { SettingsSection } from '@/types/settings'
import SettingsNav from './components/SettingsNav'
import AccountSection from './components/AccountSection'
import EmailAccountsSection from './components/EmailAccountsSection'
import AIModelsSection from './components/AIModelsSection'
import IntegrationsSection from './components/IntegrationsSection'
// Auto-Reply Rules hidden (product decision) — restore this import with the
// 'auto-reply' entry below and the nav item in SettingsNav.
// import AutoReplySection from './components/AutoReplySection'
// import CampaignDefaultsSection from './components/CampaignDefaultsSection'  // hidden — see the route entry below.
import SecuritySection from './components/SecuritySection'
// Custom AI Recipes hidden (product decision) — restore this import with the
// 'custom-ai-recipes' entry below and the nav item in SettingsNav. Unmounting
// the page is also what stops it calling GET /custom-ai-recipes on mount.
// import CustomAIRecipePage from './CustomAIRecipePage'
import TeamSection from './components/TeamSection'
import RoleAccessSection from './components/RoleAccessSection'
import WorkflowApprovalsSection from './components/WorkflowApprovalsSection'
import CampaignApprovalsSection from './components/CampaignApprovalsSection'
import CrmExportApprovalsSection from './components/CrmExportApprovalsSection'
import DeletionApprovalsSection from './components/DeletionApprovalsSection'
import AuditLogSection from './components/AuditLogSection'
import HelpCentreSection from './components/HelpCentreSection'
import SignaturesSection from './components/SignaturesSection'
import MyApprovalRequestsSection from './components/MyApprovalRequestsSection'
import FeedbackSupportSection from './components/FeedbackSupportSection'

const sectionComponents: Record<string, React.FC> = {
  'account': AccountSection,
  'email-accounts': EmailAccountsSection,
  'signatures': SignaturesSection,
  'ai-models': AIModelsSection,
  'integrations': IntegrationsSection,
  // 'auto-reply': AutoReplySection,  // hidden — see the import above.
  // A session that had this section selected falls back to AccountSection
  // via the `|| AccountSection` default below, so nothing crashes.
  // 'campaign-defaults': CampaignDefaultsSection,  // hidden — see the import above.
  // A session that had this section selected falls back to AccountSection via
  // the `|| AccountSection` default below, so nothing crashes.
  'security': SecuritySection,
  // 'custom-ai-recipes': CustomAIRecipePage,  // hidden — see the import above.
  // A session that had this section selected falls back to AccountSection via
  // the `|| AccountSection` default below, so nothing crashes and no recipe
  // request is made.
  'team': TeamSection,
  'roles-access': RoleAccessSection,
  'workflow-approvals': WorkflowApprovalsSection,
  'campaign-approvals': CampaignApprovalsSection,
  'crm-export-approvals': CrmExportApprovalsSection,
  'deletion-approvals': DeletionApprovalsSection,
  'my-approval-requests': MyApprovalRequestsSection,
  'audit-log': AuditLogSection,
  'help-centre': HelpCentreSection,
  'feedback-support': FeedbackSupportSection,
}

/**
 * The section a `/settings?section=<id>` link asks for, or null when the
 * query names nothing we render. Approval notifications deep-link this way
 * (`?section=workflow-approvals`, `?section=campaign-approvals`).
 */
export function sectionFromSearch(search: string): SettingsSection | null {
  const requested = new URLSearchParams(search).get('section')
  return requested && Object.prototype.hasOwnProperty.call(sectionComponents, requested)
    ? (requested as SettingsSection)
    : null
}

const SettingsPage: React.FC = () => {
  const { activeSection, setActiveSection, fetchConfig } = useSettingsStore()
  // One queued scroll per nav selection. `nonce` is what makes picking the same
  // search result twice re-fire, so the request never has to be cleared from
  // inside the effect (which would tear the flash down again immediately).
  const [scrollRequest, setScrollRequest] = useState<{ anchor: string | null; nonce: number } | null>(null)

  useEffect(() => {
    fetchConfig()
    // Read once on mount: the nav is the source of truth from here on. The
    // param is left in place so a reload lands on the same section.
    const requested = sectionFromSearch(window.location.search)
    if (requested) setActiveSection(requested)
  }, [])

  const requestNavigation = useUnsavedChangesStore((s) => s.requestNavigation)

  const handleNav = (section: SettingsSection, anchor?: string) => {
    requestNavigation(() => {
      setActiveSection(section)
      setScrollRequest((prev) => ({ anchor: anchor ?? null, nonce: (prev?.nonce ?? 0) + 1 }))
    })
  }

  // After the section switches (and its panel mounts past the exit/enter
  // animation), scroll to the deep-linked field and flash it. No anchor
  // requested, or no anchor wired in this panel, resets to the top rather than
  // leaving the scroll wherever the previous section had it.
  useEffect(() => {
    if (!scrollRequest) return
    const t = window.setTimeout(() => {
      revealSettingsAnchor(scrollRequest.anchor)
    }, SETTINGS_PANEL_MOUNT_MS)
    return () => window.clearTimeout(t)
  }, [scrollRequest])

  const ActiveComponent = sectionComponents[activeSection] || AccountSection

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Settings
        </h1>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-8">
        <SettingsNav activeSection={activeSection} onSectionChange={handleNav} />

        <div id={SETTINGS_PANEL_ID} className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
            >
              <ActiveComponent />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
